import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Typography,
    Collapse,
    useTheme,
    useMediaQuery
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';

import 'Styles/App.scss';
import UserContextP from 'Interfaces/Context';
import Loader from 'Components/Loader';
import DialogConfirmation from 'Components/DialogConfirmation';
import { CssTextField } from 'Components/CssTextField';
import Header from 'Components/DetailDocument/Header';
import TableProducts from 'Components/DetailDocument/TableProducts';
import InformationDocument from 'Components/DetailDocument/InformationDocument';
import { useNavigate } from 'react-router-dom';

const DetailDocument: React.FC = () => {
    const context = useContext(UserContextP);
    const navigate = useNavigate();

    const [orderLocal, setOrderLocal] = useState<any>(null);
    const [costumer, setCostumer] = useState<any>(null);
    const [text, setText] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [openResultDialog, setOpenResultDialog] = useState(false);
    const [resultMessage, setResultMessage] = useState<{ message: string; type: 'success' | 'error' }>({ message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState<null | 'approve' | 'reject'>(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    const order = useMemo(() => orderLocal ?? context?.dataCopy?.order ?? null, [orderLocal, context?.dataCopy?.order]);
    const badgeColor = useMemo(() => (order?.type === 'PurchaseOrder' ? '#79AC78' : '#173456'), [order?.type]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showSummaryMobile, setShowSummaryMobile] = useState(true);

    // Keep the default: visible on desktop, collapsed on mobile
    useEffect(() => {
        setShowSummaryMobile(!isMobile);
    }, [isMobile]);

    useEffect(() => {
        document.title = 'Actualizar estado';
        setOrderLocal(context?.dataCopy?.order ?? null);
        if (!context?.dataCopy?.order) {
            navigate(-1);
            return;
        }

        const controller = new AbortController();
        fetchCostumer(controller.signal).catch(() => { });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.dataCopy?.order]);

    const fetchCostumer = useCallback(
        async (signal?: AbortSignal) => {
            try {
                const token = context?.authenticate?.token;
                const cardCode = order?.cardCode ?? context?.dataCopy?.order?.cardCode;
                if (!token || !cardCode) return;
                const res = await axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/client`, {
                    params: { cardCode },
                    headers: { Authorization: `Bearer ${token}` },
                    signal
                });
                setCostumer(res.data?.data?.[0] ?? null);
            } catch (err) {
                console.debug('fetchCostumer error', err);
                setCostumer(null);
            }
        },
        [context?.authenticate?.token, order?.cardCode, context?.dataCopy?.order]
    );

    const submitStatus = useCallback(
        async (approve: boolean) => {
            setLoading(true);
            try {
                const token = context?.authenticate?.token;
                if (!token) throw new Error('No autorizado');
                const payload = { state: approve, id: context?.dataCopy?.order?.$key, notes: text };
                const res = await axios.put(`${process.env.REACT_APP_URL_ORDER}/api/v1/orders/update/status`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setResultMessage({ message: res.data?.message ?? (approve ? 'Orden aprobada' : 'Orden rechazada'), type: 'success' });
            } catch (err: any) {
                console.debug('submitStatus error', err);
                const msg = err?.response?.data?.message ?? 'Error al procesar la orden. Contacte soporte.';
                const isAlready = typeof msg === 'string' && msg.includes('Orden ya autorizada');
                setResultMessage({ message: msg, type: isAlready ? 'success' : 'error' });
            } finally {
                setLoading(false);
                setOpenResultDialog(true);
                setOpenConfirm(false);
                setConfirmAction(null);
            }
        },
        [context?.authenticate?.token, context?.dataCopy?.order, text]
    );

    return (
        <>
            <Loader openLoader={loading} inPage={false} redirect={true} />
            <DialogConfirmation open={openResultDialog} text={resultMessage.message} quantity={-1} type={resultMessage.type} />

            <Container maxWidth={false} disableGutters sx={{ p: 2, height: 'calc(100vh - 16px)' }}>
                <Paper sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: '100%', p: 2 }} elevation={3}>
                    <Box sx={{ flex: 1, overflow: 'auto', pr: { xs: 0, md: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => navigate(-1)}>
                                    <ArrowBackIosIcon />
                                </IconButton>
                                <Box sx={{ ml: 1 }}>
                                    <Typography variant="h6">Orden #{order?.numberOrder ?? 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">{order?.docDate ? new Date(order.docDate).toLocaleString() : ''}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ background: badgeColor, color: '#fff', px: 1.2, py: 0.4, borderRadius: 1 }}>{order?.type}</Typography>
                                {isMobile && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={showSummaryMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        onClick={() => setShowSummaryMobile(s => !s)}
                                        aria-label={showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                                        sx={{ textTransform: 'none', borderColor: 'rgba(0,0,0,0.12)' }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Grid container spacing={1}>
                            <Header order={order} costumer={costumer} color={badgeColor} />
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                            <Grid container>
                                <TableProducts order={order} color={badgeColor} />
                            </Grid>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfc' }}>
                                <Typography variant="subtitle2" color="text.secondary">Comentarios</Typography>
                                <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap', minHeight: 72, textAlign: 'justify' }}>
                                    {order?.comments && String(order.comments).trim() !== '' ? order.comments : <span style={{ color: '#666' }}>Sin comentarios</span>}
                                </Typography>
                            </Paper>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <CssTextField placeholder="Notas para el vendedor" rows={6} multiline fullWidth value={text} onChange={(e: any) => setText(e.target.value)} />
                        </Box>
                    </Box>

                    {/* Mobile: show a toggle to reveal/hide the summary. On desktop the summary is always visible. */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={showSummaryMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setShowSummaryMobile(s => !s)}
                            aria-label={showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                            sx={{ textTransform: 'none', borderColor: 'rgba(0,0,0,0.12)' }}
                        >
                            {showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                        </Button>
                    </Box>

                    <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0, mt: { xs: 2, md: 0 } }}>
                        <Box sx={{ position: { xs: 'static', md: 'sticky' }, top: { md: 16 } }}>
                            <Collapse in={showSummaryMobile} timeout={300}>
                                <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
                                    <Typography variant="subtitle1" gutterBottom>Resumen</Typography>
                                    <Grid container spacing={1}>
                                        <InformationDocument order={order} color={badgeColor} />
                                    </Grid>
                                </Paper>
                            </Collapse>

                            {/* Action buttons are always visible regardless of summary collapse */}
                            <Paper sx={{ p: 2 }} elevation={1}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Button disabled={loading} variant="contained" color="error" startIcon={<CloseIcon />} onClick={() => { setConfirmAction('reject'); setOpenConfirm(true); }} sx={{ width: '100%' }}>
                                        Rechazar
                                    </Button>
                                    <Button disabled={loading} variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => { setConfirmAction('approve'); setOpenConfirm(true); }} sx={{ width: '100%' }}>
                                        Aprobar
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </Paper>
            </Container>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} fullWidth maxWidth="sm">
                <DialogTitle>{confirmAction === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 1 }}>{confirmAction === 'approve' ? '¿Está seguro de que desea aprobar esta orden?' : '¿Está seguro de que desea rechazar esta orden?'}</Typography>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" gutterBottom>Notas</Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', minHeight: 96, whiteSpace: 'pre-wrap', overflow: 'auto' }}>
                            <Typography variant="body2" color={text ? 'text.primary' : 'text.secondary'}>
                                {text && text.length > 0 ? text : 'Sin notas'}
                            </Typography>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)} disabled={loading}>Cancelar</Button>
                    <Button variant="contained" color={confirmAction === 'approve' ? 'success' : 'error'} disabled={loading} onClick={() => {
                        if (confirmAction === 'approve') submitStatus(true);
                        else if (confirmAction === 'reject') submitStatus(false);
                    }}>{confirmAction === 'approve' ? 'Aprobar' : 'Rechazar'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DetailDocument;